import { Injectable, TemplateRef } from "@angular/core";
import { LocationInformationComponent } from "@app/directives/_misc/location-information/location-information.component";
import { VideoDownloadStatusComponent } from "@app/directives/_misc/video-download-status/video-download-status.component";
import { VehicleRegComponent } from "@app/directives/_vehicle/vehicleReg/vehicleReg.component";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { IVehicle } from "@app/interfaces/IVehicle";
import { DateService, DateUnit } from "@app/services/date.service";
import { GeocodeService } from "@app/services/geocode.service";
import { LocationInformationService } from "@app/services/location-information/location-information.service";
import { TranslationService } from "@app/services/translation.service";
import { CheckboxCell } from "../_components/cells/checkbox-cell/checkbox-cell.component";
import { EnumCellComponent } from "../_components/cells/enum-cell/v-enum-cell.component";
import { PercentageBarCellComponent, PercentageBarModel } from "../_components/cells/percentage-bar/percentage-bar-cell.component";
import { RagCellBehavior, RagCellComponent } from "../_components/cells/rag-cell/rag-cell.component";
import { DateCell } from "../_components/cells/stacked-date/date-cell.component";
import { StatusCell } from "../_components/cells/status-cell/status-cell.component";
import { VTableHeaderType } from "../_enums/VTableHeaderType.enum";
import { VTableRowStatus } from "../_enums/VTableRowStatus.enum";
import { ILocationCellOptions } from "../_models/ILocationCellOptions";
import { IVTable } from "../_models/IVTable";
import { IVTableCell } from "../_models/IVTableCell";
import { IVTableCellOptions } from "../_models/IVTableCellOptions";
import { IVTableColumn } from "../_models/IVTableColumn";
import { IVTableHeader } from "../_models/IVTableHeader";
import { IVTableHeaderOptions } from "../_models/IVTableHeaderOptions";
import { IVTableRow } from "../_models/IVTableRow";
import { VideoDownloadIcons } from "@app/enum/VideoDownloadIcons";

export enum FallbackCellType {
    NA,
    Dashed,
    Blank,
    Unknown,
    Never
}

export interface IDateCellOptions {
    showRelativeTime?: boolean;
    showErrorIfBeforeDate?: Date;
    showWarningIfBeforeDate?: Date;
    dateTime?: boolean;
    fallbackCellType?: FallbackCellType;
    validationCutOff?: Date; // If date is earlier than this then it should be classed as invalid; helps rule out automatic dates such as 1900-01-01 and 0001-01-01
}

const SORT_NULL_VALUE = -99999999999;


@Injectable({
    providedIn: 'root'
})
export class VTableService {
    constructor(private readonly dateService: DateService,
        private readonly geocodeService: GeocodeService,
        private readonly locationInformationService: LocationInformationService,
        private readonly translationService: TranslationService) { }

    /**
    * @description Creates an array of table rows based on column configuration and data array
    * @template TModel - The object type for the data param
    * @param {TModel[]} data The array of data to convert to table rows
    * @param {IVTableColumn<TModel>[]} columns The array of column configurations
    * @param {number} [preSelectedRow] (optional) Preselect a row in a multi select table 
    * @returns {IVTableRow[]}
    */
    createTableRows<TModel>(data: TModel[], columns: IVTableColumn<TModel>[], preSelectedRow?: number): IVTableRow[] {
        if (!data) {
            return;
        }
        const rows: IVTableRow[] = []

        data.forEach(d => {
            rows.push(this.createTableRow(d, columns));
        })

        if (preSelectedRow != null) {
            rows[preSelectedRow].selected = true;
        }

        return rows;
    }

    /**
    * @description Create a single table row based on column configuration and data object
    * @template TModel - The object type for the data param
    * @param {TModel[]} data The data object to convert to a table row
    * @param {IVTableColumn<TModel>[]} columns The array of column configurations
    * @returns {IVTableRow}
    */
    createTableRow<TModel>(data: TModel, columns: IVTableColumn<TModel>[]): IVTableRow {
        const row: IVTableRow = {
            cells: [],
            data: data,
        };

        columns.forEach(column => {
            const cell: IVTableCell = column.createCellFunction(data);
            row.cells.push(cell);
            if (column.header.searchable) {
                row.searchString += `||||${cell.searchData ? cell.searchData.toLowerCase() : ''}`;
            }
        })

        row.disableSelect = row.cells.some(cell => cell.disableRow);

        return row;
    }

    /**
    * @description Add a new table row to an existing array of rows
    * @template TModel - The object type for the data param
    * @param {TModel[]} data The data object to convert to a table row
    * @param {IVTableColumn<TModel>[]} columns The array of column configurations
    * @param {IVTableRow[]} rows: The array of rows to append the new row to
    * @returns {void}
    */
    addNewRow<TModel>(data: TModel, columns: IVTableColumn<TModel>[], rows: IVTableRow[]): void {
        if (!rows?.length) {
            return;
        }

        const newRow = this.createTableRow(data, columns);
        rows.push(newRow);
    }

    // Sorting and Filtering

    /**
    * @description Sort table rows by a specific column
    * @param {IVTable} table The table to sort
    * @param {IVTableHeader} header The header (column) to sort by
    * @returns {IVTable}
    */
    sortColumn(table: IVTable, header: IVTableHeader): IVTable {
        if (!table?.rows) {
            return;
        }

        const columnIndex = header.id;

        switch (header.sortType) {
            case SortType.Date:
                table.rows.sort((a, b) => {
                    const sortDataA = a.cells[columnIndex].sortData as any;
                    const sortDataB = b.cells[columnIndex].sortData as any;

                    if (sortDataA === SORT_NULL_VALUE && sortDataB !== SORT_NULL_VALUE) {
                        return 0;
                    }
                    if (sortDataA !== SORT_NULL_VALUE && sortDataB === SORT_NULL_VALUE) {
                        return -1;
                    }
                    return header.sortDirection === SortDirection.Ascending
                        ? sortDataA - sortDataB
                        : sortDataB - sortDataA;
                });
                break;
            case SortType.Number:
            case SortType.Boolean:
                table.rows.sort((a, b) => {
                    return header.sortDirection === SortDirection.Ascending ?
                        (a.cells[columnIndex].sortData as any) - (b.cells[columnIndex].sortData as any) :
                        (b.cells[columnIndex].sortData as any) - (a.cells[columnIndex].sortData as any)
                });
                break;
            case SortType.String:
                table.rows.sort((a, b) => {
                    if (String(a.cells[columnIndex].sortData) < String(b.cells[columnIndex].sortData)) {
                        return header.sortDirection === SortDirection.Ascending ? -1 : 1;
                    }
                    if (String(a.cells[columnIndex].sortData) > String(b.cells[columnIndex].sortData)) {
                        return header.sortDirection === SortDirection.Ascending ? 1 : -1;
                    }
                    return 0;
                });
                break;
        }

        return table;
    }

    /**
    * @description Filter table rows according to pre-filtered data. Any rows that don't have a corresponding data item are hidden.
    * @param {IVTable} table The table to filter
    * @param {any[]} filteredData The array of data that has been filtered eg. in filterBarComponent
    * @returns {void}
    */
    filterRows(table: IVTable, filteredData: any[]): void {
        if (!table?.rows) {
            return;
        }
        const tableRowMap = new Map(table.rows.map(row => [row.data.Id, row]))
        table.rows.forEach(row => row.hide = true);

        if (!filteredData.length) {
            return;
        }

        filteredData.forEach(data => {
            const row = tableRowMap.get(data["Id"]);
            if (row) {
                row.hide = false;
            }
        });
    }

    /**
    * @description Generate a new table column using the supplied configuration
    * @template TModel - The object type for the data the table is displaying
    * @param {IVTableHeader} header The configuration options for the header
    * @param {(data: TModel) => IVTableCell} createCellFunction The method that will generate the table cell, including the data to be displayed and any configuration options
    * @returns {IVTableColumn<TModel>}
    */
    createTableColumn<TModel>(header: IVTableHeader, createCellFunction: (data: TModel) => IVTableCell): IVTableColumn<TModel> {
        return {
            header: header,
            createCellFunction: createCellFunction
        }
    }

    // Header creation

    /**
    * @description Generate a new table header using the supplied configuration
    * @param {IVTableHeaderOptions} options The configuration options for the header
    * @param {VTableHeaderType} [type = VTableHeaderType.Default] (optional) Determines the visual appearance of the header, defaults to VTableHeaderType.Default
    * @returns {IVTableHeader}
    */
    createTableHeader(options: IVTableHeaderOptions, type: VTableHeaderType = VTableHeaderType.Default): IVTableHeader {
        const translation = options.tKey || options.translationKey;

        const header: IVTableHeader = {
            title: translation ? this.translationService.translate(translation) : options.title || "",
            type: type,
            width: options.width,
            minWidth: options.minWidth,
            searchable: options.searchable != undefined ? options.searchable : true,
            sortable: options.sortable != undefined ? options.sortable : true,
            sortDirection: options.sortDirection | SortDirection.Ascending,
            sorted: options.sorted,
            sortType: options.sortType || SortType.String,
            classes: options.classes || "",
            minTableWidth: options.minTableWidth,
            excludeFromExport: options.excludeFromExport,
            exportOnly: options.exportOnly,
            canEdit: options.canEdit,
            hidden: options.hidden || false,
            measurementCategory: options.measurementCategory
        }

        if (header.sortable) {
            header.classes += " sortable";
        }

        return header;
    }

    /**
    * @description Generate a new table header with a default configuration for vehicle headers: {title: "Vehicle", width: 100, sortType: SortType.String, type: VTableHeaderType.Vehicle}
    * @param {IVTableHeaderOptions} options Any extra configuration options for the header.
    * @returns {IVTableHeader}
    */
    createVehicleTableHeader(options?: IVTableHeaderOptions): IVTableHeader {
        return this.createTableHeader({ tKey: 'Common.Vehicle', width: 100, sortType: SortType.String, ...options }, VTableHeaderType.Vehicle);
    }

    /**
    * @description Generate a new table header with a default configuration for status headers
    * @param {IVTableHeaderOptions} options Any extra configuration options for the header.
    * @example {title: "Status", width: 65, sortable: true, sorted: true, sortDirection: SortDirection.Descending, sortType: SortType.Number, classes: 'status', type: VTableHeaderType.Status}
    * @returns {IVTableHeader}
    */
    createStatusTableHeader(options?: IVTableHeaderOptions): IVTableHeader {
        return this.createTableHeader({ tKey: 'Common.Status', width: 55, sortable: true, sorted: true, sortDirection: SortDirection.Descending, sortType: SortType.Number, classes: 'status', ...options }, VTableHeaderType.Status);
    }

    /**
    * @description Generate a new table header with a default configuration for date headers: { title: "Date", width: 150, sortType: SortType.Date, type: VTableHeaderType.Date}
    * @param {IVTableHeaderOptions} options Any extra configuration options for the header.
    * @returns {IVTableHeader}
    */
    createDateTableHeader(options?: IVTableHeaderOptions): IVTableHeader {
        return this.createTableHeader({ tKey: 'Common.Date', width: 150, sortType: SortType.Date, ...options }, VTableHeaderType.Date)
    }

    /**
    * @description Generate a new table header with a default configuration for location information headers: {title: "Location", sortable: false, width: 200}
    * @param {IVTableHeaderOptions} options Any extra configuration options for the header.
    * @returns {IVTableHeader}
    */
    createLocationInformationHeader(options?: IVTableHeaderOptions): IVTableHeader {
        return this.createTableHeader({ tKey: 'Common.Location', sortable: false, width: 200, ...options })
    }

    /**
    * @description Generate a new table header containing a checkbox for multi-select tables. Will be called automatically when table configuration specifies multi-select
    * @returns {IVTableHeader}
    */
    createCheckboxHeader(): IVTableHeader {
        return this.createTableHeader({
            width: 50,
            sortable: false
        }, VTableHeaderType.Checkbox)
    }

    // Cell creation


    /**
    * @description Generate a new table cell using the supplied configuration
    * @param {any} data The data the cell relates to. This can be any type but undefined or null will result in a dashed or N/A cell.
    * @param {IVTableCellOptions} [options = {}] (optional) Any configuration options to apply to the cell
    * @param {FallbackCellType} [fallbackType = FallbackCellType.Dashed] (optional) Determines what is displayed in the cell in the case of null or undefined data
    * @returns {IVTableCell}
    */
    createTableCell(data: any, options: IVTableCellOptions = {}, fallbackType: FallbackCellType = FallbackCellType.Dashed): IVTableCell {
        if (!data && data !== 0 ||
            !String(data).length ||
            (typeof (data) === 'object' && Object.values(data).length > 0 && Object.values(data).every(item => !item)) // Covers when a template object has null for every key
        ) {
            let cellContent: string;
            switch (fallbackType) {
                case FallbackCellType.NA:
                    cellContent = 'N/A';
                    break;

                case FallbackCellType.Dashed:
                    cellContent = '--';
                    break;

                case FallbackCellType.Blank:
                    cellContent = "";
                    break;
                case FallbackCellType.Unknown:
                    cellContent = this.translationService.t("Common.Unknown")
                    break;
                case FallbackCellType.Never:
                    cellContent = this.translationService.t("Common.Never")
                    break;
                default:
                    cellContent = "--";
                    break;
            }
            
            return {
                data: cellContent,
                searchData: "",
                exportData: "",
                sortData: SORT_NULL_VALUE, //TODO - there must be a better way...
                classes: "text-muted " + options.classes,
                disableRow: options.disableRow,
                disableCell: options.disableCell
            }

        }

        const cell: IVTableCell = {
            data: data,
            sortData: options?.sortData || (options?.sortData !== 0 ? data : options?.sortData),
            searchData: options?.searchData?.toString() || options?.sortData?.toString() || options?.exportData?.toString() || data.toString(),
            exportData: options?.exportData || data,
            classes: options?.classes,
            exportDataMethod: options.exportDataMethod,
            component: options.component,
            componentInputs: options.componentInputs,
            componentOutputs: options.componentOutputs,
            template: options.template,
            tooltip: options.tooltip,
            icon: options.icon,
            disableRow: options.disableRow,
            disableCell: options.disableCell
        }
        return cell;
    }

    /**
    * @description Helper method to generate a new table cell with default configuration for using an angular template
    * @param {any} data The data object required by the supplied TemplateRef
    * @param {TemplateRef<any>} template The templateRef to be injected into the cell
    * @param {IVTableCellOptions} [options = {}] (optional) Any configuration options to apply to the cell
    * @returns {IVTableCell}
    */
    createTableTemplateCell(data: any, template: TemplateRef<any>, options?: IVTableCellOptions): IVTableCell {
        return this.createTableCell(data, { ...options, template: template })
    }

    /**
    * @description Helper method to generate a new table cell with default configuration for displaying a row status. Displays the status with a StatusCell component.
    * @see StatusCell
    * @param {VTableRowStatus} data The status to display - will show an icon relating to the row status
    * @param {IVTableCellOptions} [options = {}] (optional) Any configuration options to apply to the cell
    * @returns {IVTableCell}
    */
    createStatusCell(data: VTableRowStatus, options?: IVTableCellOptions): IVTableCell {
        return this.createTableCell(data, { searchData: null, exportDataMethod: () => this.exportStatusMethod(data), component: StatusCell, componentInputs: { data: data }, sortData: options?.sortData || data }, FallbackCellType.Dashed);
    }

    /**
    * @description Helper method to generate a new table cell with default configuration for displaying a vehicle registration. Displays a VehicleRegComponent.
    * @see VehicleRegComponent
    * @param {IVehicle} vehicle The vehicle to display
    * @param {number} [vehicleId] (optional) Can be used if no vehicle object exists
    * @param {IVTableCellOptions} [options = {}] (optional) Any configuration options to apply to the cell
    * @returns {IVTableCell}
    */
    createVehicleRegCell(vehicle: IVehicle, vehicleId?: number, allowNonDatabaseVehicle: boolean = false): IVTableCell {
        let regString: string = vehicle?.Reg || vehicle?.ShortReg || "N/A";
        let regSearchString: string = `${vehicle?.Reg}||||${vehicle?.ShortReg}` || "N/A";
        return this.createTableCell(regString, { sortData: regString, exportData: regString, searchData: regSearchString, component: VehicleRegComponent, componentInputs: { vehicle: vehicle, vehicleId: vehicleId, allowNonDatabaseVehicle: allowNonDatabaseVehicle } });
    }

    /**
    * @description Helper method to generate a new table cell showing a resource location (address or geofence). Displays a LocationInformationComponent.
    * @see LocationInformationComponent
    * @param {ILocationCellOptions} cellOptions Contains the data (eg. plotId, IPlot, lat/lng) that is required by the LocationInformationComponent to retrieve the location
    * @returns {IVTableCell}
    */
    createLocationInformationCell(cellOptions: ILocationCellOptions): IVTableCell {
        return this.createTableCell(
            cellOptions.plotId || cellOptions.plot?.Id || cellOptions.latLng,
            {
                searchData: null,
                exportDataMethod: () => this.exportLocationInformationMethod(cellOptions.plotId || cellOptions.plot?.Id),
                component: LocationInformationComponent,
                componentInputs: {
                    plot: cellOptions.plot || { Id: cellOptions.plotId },
                    latLng: cellOptions.latLng,
                    locationInformation: cellOptions.locationInformation
                }
            });
    }

    /**
    * @description Helper method to generate a new table cell showing a checkbox. Displays a CheckboxCell component.
    * @see CheckboxCell
    * @param {boolean} [selected = false] (optional) Whether the checkbox should be pre-selected. Defaults to false.
    * @returns {IVTableCell}
    */
    createCheckboxCell(selected = false): IVTableCell {
        return this.createTableCell(selected, { component: CheckboxCell, componentInputs: { data: selected } });
    }

    /**
    * @description Helper method to generate a new table cell showing a red/amber/green indicator. Displays a RagCellComponent.
    * @see RagCellComponent
    * @param {any} displayData 
    * @param {T} item 
    * @param {RagCellBehavior<T>} behavior 
    * @returns {IVTableCell}
    */
    createRagCell<T>(displayData: any, item: T, behavior: RagCellBehavior<T>): IVTableCell {
        return this.createTableCell(displayData, { component: RagCellComponent, componentInputs: { item: item, behavior: behavior, displayData: displayData }, });
    }

        /**
    * @description Helper method to generate a new table cell showing a percentage bar. Displays a PercentageBarCellComponent.
    * @see PercentageBarCellComponent
    * @param {PercentageBarModel} data 
    * @returns {IVTableCell}
    */
    createPercentageBarCell(data: PercentageBarModel): IVTableCell {
        let dataToSort = 0;

        switch (true) {
            case (!!data.negativeSegment && !!data.positiveSegment):
                dataToSort = data.negativeSegment.count / data.positiveSegment.count;
                break;
            
            case (!!data.negativeSegment && !!data.neutralSegment):
                dataToSort = data.negativeSegment.count / data.neutralSegment.count;
                break;

            case (!!data.neutralSegment && !!data.positiveSegment):
                dataToSort = data.neutralSegment.count / data.positiveSegment.count;
                break;

            case (!!data.negativeSegment && !!data.neutralSegment):
                dataToSort = data.negativeSegment.count / data.positiveSegment.count;
                break;
        }

        if (Number.isNaN(dataToSort)) {
            dataToSort = -1;
        }

        return this.createTableCell(dataToSort, { component: PercentageBarCellComponent, componentInputs: { model: data } });
    }

    /**
    * @description Helper method to generate a new table cell showing a date. Displays a DateCell component.
    * @see DateCell
    * @param {Date | string} date The date to be displayed
    * @param {IDateCellOptions} [options] (optional) Configuration options such as error status and showing relative time (time ago).
    * @returns {IVTableCell}
    */
    createDateCell(date: Date | string, options?: IDateCellOptions): IVTableCell {
        const parsedDate = new Date(date);

        if (isNaN(parsedDate.getTime()) || parsedDate.getFullYear() < 1000 || parsedDate.getFullYear() > 2100 || date == null) {
            // If year is < 1000 it is probably not an accurate date, sometimes we get 0001-01-01
            return this.createTableCell(null, { sortData: parsedDate?.getTime() || SORT_NULL_VALUE, classes: 'text-muted' }, options?.fallbackCellType);
        }

        if (options?.validationCutOff && this.dateService.date1IsAfterDate2(options.validationCutOff, parsedDate)) {
            return this.createTableCell(this.translationService.t("Common.Unknown"), { sortData: 0, classes: 'text-muted' });
        }

        return this.createTableCell(parsedDate, {
            searchData: null,
            sortData: parsedDate.getTime(),
            exportDataMethod: () => this.exportDateMethod(parsedDate, options?.dateTime ? options?.dateTime : true),
            component: DateCell,
            componentInputs: {
                date: parsedDate,
                showErrorIfBeforeDate: options?.showErrorIfBeforeDate,
                showWarningIfBeforeDate: options?.showWarningIfBeforeDate,
                showRelativeTime: options?.showRelativeTime,
                dateTime: options?.dateTime ? options?.dateTime : true
            }
        });
    }

    /**
    * @description Helper method to generate a new table cell showing a duration.
    * @param {number} value The duration to convert. Can be any unit of time such as seconds, days etc.
    * @param {DateUnit} [dateUnit = DateUnit.Ms] (optional) The unit of time that the value param is measured in. Defaults to ms.
    * @returns {IVTableCell}
    */
    createDurationCell(value: number, dateUnit: DateUnit = DateUnit.Ms): IVTableCell {
        const milliseconds = this.dateService.convertToMilliseconds(dateUnit, value);

        const displayValue = this.dateService.formatTimeFromMilliseconds(milliseconds, { style: "narrow" });

        return this.createTableCell(displayValue, { sortData: milliseconds });
    }

    /**
    * @description Helper method to generate a new table cell showing a duration, based on given dates.
    * @param {Date} start Start Date
    * @param {Date} end End Date
    * @returns {IVTableCell}
    */
    createDurationCellFromDates(start: Date, end: Date): IVTableCell {
        start = new Date(start);
        end = new Date(end);

        const sortData = end.getTime() - start.getTime();
        const displayValue = this.dateService.formatDuration(start, end);

        return this.createTableCell(displayValue, { sortData: sortData });
    }

    /**
    * @description Helper method to generate a new table cell showing translated enum.
    * @template {T} - The enum type
    * @param {T} enumType The enum type
    * @param {string} enumName The enum type as a string
    * @param {number} enumValue The value as a number
    * @example createEnumCell(VehicleType, "VehicleType", resource.VehicleType)
    * @returns {IVTableCell}
    */
    createEnumCell<T>(enumType: T, enumName: string, enumValue: number): IVTableCell {
        return this.createTableCell(enumValue, {
            sortData: enumValue,
            component: EnumCellComponent,
            componentInputs: {
                enumType,
                enumName,
                enumValue
            },
            exportDataMethod: () => Promise.resolve(this.translationService.tEnum(enumType, enumName, enumValue))
        })
    }

    /**
     * @description Helper method to generate a new table cell that displays a label with an icon next to it.
     * @param {string} label The label to be displayed
     * @param {VideoDownloadIcons[]} autoDownload The list of icons to be displayed
     * @example createVideoDownloadStatusCell("Requested", [VideoDownloadIcons.AutoDownload])
     * @returns {IVTableCell}
     */
    createVideoDownloadStatusCell(label: string, icons: VideoDownloadIcons[]): IVTableCell {
        {           
            return this.createTableCell(label, {
                exportData: label,
                component: VideoDownloadStatusComponent,
                componentInputs: {
                    label: label,
                    icons: icons
                }
            })            
        }
    }

    /**
    * @description Generate a unique Id for a table based on headers. Used to generate a local storage key to save user table preferences
    * @param {IVTableColumn<any>[]} tableColumns
    * @returns {string}
    */
    getTableId(tableColumns: IVTableColumn<any>[]): string {
        return tableColumns.map(c => c.header.title).join("").slice(-0, 40);
    }

    /**
    * @description Retrieve the user's column preferences from local storage
    * @param {IVTableColumn<any>[]} tableColumns
    * @returns {IVTableColumn<any>[]}
    */
    getTableColumnPreferences(tableColumns: IVTableColumn<any>[]): IVTableColumn<any>[] {
        const existingPreferences = window.localStorage.getItem(this.getTableId(tableColumns));
        if (existingPreferences) {
            return JSON.parse(existingPreferences);
        }

        return tableColumns;
    }

    /**
    * @description Check if any table headers are editable to trigger column editor button
    * @param {IVTableColumn<any>[]} tableColumns
    * @returns {boolean}
    */
    getCanEditColumns(tableColumns: IVTableColumn<any>[]): boolean {
        return tableColumns.some(col => col.header.canEdit);
    }

    // Cell Export Methods

    private exportLocationInformationMethod(plotId: number): Promise<string> {
        return new Promise((resolve) => {
            return this.geocodeService.getLocationInformationFromPlotId(plotId).then(locationInformation => {
                if (!locationInformation) {
                    resolve("N/A");
                }

                if (locationInformation.GeofenceIds.length) {
                    resolve(
                        this.locationInformationService.getGeofenceNamesString(locationInformation)
                    );
                } else {
                    resolve(locationInformation.LocationDescription);
                }
            }).catch((error) => {
                console.error(`Error retrieving location: ${error}`);
                resolve("--");
            });
        })
    }

    private exportStatusMethod(status: VTableRowStatus): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!status) {
                resolve('--');
            }

            const statusString = this.translationService.tEnum(VTableRowStatus, 'VTableRowStatus', status);
            resolve(statusString);
        })
    }

    private exportDateMethod(date: Date, dateTime: boolean): Promise<string> {
        return new Promise((resolve, reject) => {
            resolve(this.dateService.formatDate(date, { dateTime: dateTime }));
        })
    }    
}
